import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import PublicNavbar from "../components/PublicNavbar";
import SignaturePad from "../components/SignaturePad";
import { api, formatEUR, formatEURDecimal, formatDateFR } from "../lib/api";
import { Loader2, ShieldCheck, CheckCircle2, FileText, AlertTriangle, Printer } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const STATUS_LABELS = {
  pending_payment: { label: "En attente de paiement", color: "bg-amber-100 text-amber-800" },
  awaiting_signatures: { label: "En attente de signatures", color: "bg-violet-100 text-violet-800" },
  signed_client: { label: "Signé par le client", color: "bg-blue-100 text-blue-800" },
  signed_vendor: { label: "Signé par le loueur", color: "bg-blue-100 text-blue-800" },
  signed_both: { label: "Signé par les deux parties", color: "bg-emerald-100 text-emerald-800" },
  active: { label: "Actif", color: "bg-emerald-100 text-emerald-800" },
  completed: { label: "Terminé", color: "bg-slate-100 text-slate-600" },
};

export default function Contract() {
  const { bookingId } = useParams();
  const { user } = useAuth();
  const [contract, setContract] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [showPad, setShowPad] = useState(false);

  useEffect(() => {
    api.get(`/contracts/${bookingId}`)
      .then(({ data }) => { setContract(data.contract); setRole(data.role); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [bookingId]);

  const handleSign = async (signatureData) => {
    setSigning(true);
    try {
      const { data } = await api.post(`/contracts/${bookingId}/sign`, { signature_data: signatureData });
      setContract((c) => ({
        ...c,
        status: data.status,
        [`${role}_signature`]: { data_base64: signatureData, signed_at: new Date().toISOString(), signer_name: user?.name },
      }));
      setShowPad(false);
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur lors de la signature");
    } finally {
      setSigning(false);
    }
  };

  if (loading) return <Page><div className="py-32 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div></Page>;
  if (!contract) return <Page><div className="py-20 text-center text-slate-500">Contrat introuvable.</div></Page>;

  const st = STATUS_LABELS[contract.status] || STATUS_LABELS.awaiting_signatures;
  const isClient = role === "client";
  const alreadySigned = isClient ? !!contract.client_signature : !!contract.vendor_signature;
  // Seul le client peut signer le contrat. Le loueur a sa signature auto-apposée depuis Paramétrages.
  const canSign = isClient && !contract.client_signature && contract.status !== "pending_payment";
  const vendorHasNoSignatureYet = !contract.vendor_signature && !isClient;

  return (
    <Page>
      <div className="max-w-[850px] mx-auto px-5 sm:px-8 py-8">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-violet-600" />
              <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-slate-900" data-testid="contract-title">Contrat de location{contract.reference ? ` ${contract.reference}` : ""}</h1>
            </div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${st.color}`} data-testid="contract-status">{st.label}</span>
              {contract.booking_reference && <span className="text-xs text-slate-400 font-mono">Réservation {contract.booking_reference}</span>}
            </div>
          </div>
          <button
            onClick={() => window.print()}
            className="shrink-0 h-10 px-4 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors inline-flex items-center gap-2"
            data-testid="contract-print-btn"
          >
            <Printer className="h-3.5 w-3.5" /> Imprimer
          </button>
        </div>

        {/* Contract body */}
        <div id="contract-print-area" className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 space-y-8" data-testid="contract-body">
          {/* Parties */}
          <Section title="Parties">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <div className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400 mb-2">Loueur</div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-violet-600" />
                  <span className="text-sm font-medium text-slate-900">Loueur certifié Junago</span>
                </div>
                <div className="text-sm text-slate-600 mt-1">{contract.vendor_company}</div>
                <div className="text-sm text-slate-500">{contract.vendor_city}</div>
              </div>
              <div>
                <div className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400 mb-2">Locataire</div>
                <div className="text-sm font-medium text-slate-900">{contract.client_name}</div>
                {contract.main_driver?.license_number && (
                  <div className="text-xs text-slate-500 mt-0.5">Permis n° {contract.main_driver.license_number}</div>
                )}
                {(contract.additional_drivers || []).length > 0 && (
                  <div className="mt-2">
                    <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-slate-400">Conducteur{contract.additional_drivers.length > 1 ? "s" : ""} supplémentaire{contract.additional_drivers.length > 1 ? "s" : ""}</div>
                    {contract.additional_drivers.map((d, i) => (
                      <div key={i} className="text-sm text-slate-700 mt-0.5">
                        {d.name}{d.license_number ? <span className="text-xs text-slate-500"> · Permis n° {d.license_number}</span> : null}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Section>

          {/* Vehicle */}
          <Section title="Véhicule">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <Row label="Marque / Modèle" value={`${contract.vehicle_brand} ${contract.vehicle_model}`} />
              <Row label="Catégorie" value={contract.vehicle_category} />
              <Row label="Immatriculation" value={contract.vehicle_plate || "—"} />
              <Row label="Lieu" value={contract.vehicle_city} />
            </div>
          </Section>

          {/* Dates & pricing */}
          <Section title="Location">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <Row label="Début" value={`${formatDateFR(contract.start_date)}${contract.start_time ? ` à ${contract.start_time}` : ""}`} />
              <Row label="Fin" value={`${formatDateFR(contract.end_date)}${contract.end_time ? ` à ${contract.end_time}` : ""}`} />
              <Row label="Durée" value={`${contract.days} jour${contract.days > 1 ? "s" : ""}`} />
              {contract.pickup_location_name && <Row label="Agence de départ" value={contract.pickup_location_name} />}
              {contract.return_location_name && <Row label="Agence de retour" value={contract.return_location_name} />}
            </div>
          </Section>

          <Section title="Tarif">
            <div className="grid grid-cols-2 gap-4 text-sm" data-testid="contract-client-pricing">
              <Row label="Tarif journalier" value={formatEUR(contract.price_per_day)} />
              {contract.subtotal != null && <Row label="Sous-total" value={formatEUR(contract.subtotal)} />}
              {(contract.options || []).map((o) => <Row key={o.key} label={o.label} value={`+ ${formatEUR(o.amount)}`} />)}
              {(contract.discount_amount || 0) > 0 && <Row label="Remise" value={`− ${formatEUR(contract.discount_amount)}`} />}
              {(contract.vat_amount || 0) > 0 && <Row label={`TVA (${contract.vat_rate}%)`} value={formatEUR(contract.vat_amount)} />}
              <Row label="Total" value={formatEUR(contract.total)} bold />
              {contract.legal_snapshot?.show_deposit !== false && contract.caution_amount ? (
                <Row label="Caution (empreinte)" value={formatEUR(contract.caution_amount)} />
              ) : null}
              {contract.legal_snapshot?.franchise_amount ? (
                <Row label="Franchise en cas de dommages" value={formatEUR(contract.legal_snapshot.franchise_amount)} />
              ) : null}
              {contract.legal_snapshot?.included_km_per_day ? (
                <Row label="Kilométrage inclus" value={`${contract.legal_snapshot.included_km_per_day} km/jour`} />
              ) : null}
              {contract.legal_snapshot?.extra_km_price ? (
                <Row label="Km supplémentaire" value={`${formatEURDecimal(contract.legal_snapshot.extra_km_price)}/km`} />
              ) : null}
            </div>
          </Section>

          {/* Conditions (ou texte personnalisé du loueur) */}
          <Section title={contract.custom_body ? "Conditions personnalisées" : "Conditions"}>
            {contract.custom_body ? (
              <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="contract-custom-body">
                {contract.custom_body}
              </div>
            ) : contract.legal_snapshot?.contract_text ? (
              <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="contract-legal-text">
                {contract.legal_snapshot.contract_text}
              </div>
            ) : (
              <ul className="space-y-2.5">
                {(contract.conditions || []).map((c, idx) => (
                  <li key={`condition-${idx}`} className="flex items-start gap-2 text-sm text-slate-600">
                    <CheckCircle2 className="h-4 w-4 text-violet-500 mt-0.5 shrink-0" />
                    {c}
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {/* Signatures */}
          {contract.legal_snapshot?.cgv && (
            <Section title="Conditions générales de location">
              <div className="text-xs text-slate-600 whitespace-pre-wrap leading-relaxed" data-testid="contract-cgv">{contract.legal_snapshot.cgv}</div>
            </Section>
          )}

          {contract.legal_snapshot?.mentions && (
            <div className="text-[10px] text-slate-400 whitespace-pre-wrap leading-relaxed border-t border-slate-100 pt-4" data-testid="contract-mentions">{contract.legal_snapshot.mentions}</div>
          )}

          <Section title="Signatures">
            <div className="grid sm:grid-cols-2 gap-6">
              <SigBlock label="Loueur" sig={contract.vendor_signature} stamp={contract.vendor_stamp} isVendor autoNotice={!contract.vendor_signature && !isClient} />
              <SigBlock label="Client" sig={contract.client_signature} />
            </div>

            {vendorHasNoSignatureYet && (
              <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 flex items-start gap-2" data-testid="vendor-no-signature-warning">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-sm text-slate-700">
                  Aucune signature n'est enregistrée sur votre profil. Configurez-la dans <Link to="/vendor/parametrages" className="font-semibold text-violet-700 underline">Paramétrages → Signature</Link>. Elle sera automatiquement apposée sur ce contrat.
                </div>
              </div>
            )}

            {canSign && !showPad && (
              <button onClick={() => setShowPad(true)} className="mt-6 w-full h-11 inline-flex items-center justify-center gap-2 rounded-xl bg-violet-600 text-white font-semibold text-sm hover:bg-violet-700 transition-colors" data-testid="sign-contract-btn">
                Signer le contrat
              </button>
            )}

            {canSign && showPad && (
              <div className="mt-6">
                <div className="text-sm text-slate-600 mb-3">Dessinez votre signature ci-dessous :</div>
                <SignaturePad onConfirm={handleSign} disabled={signing} />
                <div className="text-[10px] text-slate-400 mt-2">
                  En signant, vous acceptez les conditions du contrat. Votre adresse IP et l'horodatage seront enregistrés à titre de preuve.
                </div>
              </div>
            )}

            {isClient && contract.client_signature && (
              <div className="mt-4 text-sm text-emerald-600 flex items-center gap-1.5" data-testid="client-signed-notice">
                <CheckCircle2 className="h-4 w-4" /> Vous avez signé ce contrat.
              </div>
            )}
            {!isClient && (
              <div className="mt-4 text-xs text-slate-500" data-testid="vendor-auto-signed-notice">
                {contract.vendor_signature
                  ? "Votre signature a été automatiquement apposée depuis votre profil."
                  : "Configurez votre signature dans Paramétrages pour qu'elle soit auto-apposée sur les prochains contrats."}
              </div>
            )}
          </Section>
        </div>
      </div>
    </Page>
  );
}

function Page({ children }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <PublicNavbar />
      {children}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-xs tracking-[0.2em] uppercase font-bold text-violet-600 mb-3">{title}</div>
      {children}
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div>
      <div className="text-slate-500 text-xs">{label}</div>
      <div className={`mt-0.5 ${bold ? "font-semibold text-slate-900" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

function SigBlock({ label, sig, stamp, isVendor }) {
  return (
    <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/50" data-testid={`sigblock-${label.toLowerCase()}`}>
      <div className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400 mb-2">{label}</div>
      {sig ? (
        <>
          <div className="relative inline-flex items-center gap-3">
            <img src={sig.data_base64} alt="Signature" className="h-16 object-contain" data-testid={`sig-${label.toLowerCase()}`} />
            {isVendor && stamp && (
              <img src={stamp} alt="Tampon" className="h-16 object-contain opacity-90" data-testid="vendor-stamp-img" />
            )}
          </div>
          <div className="text-[10px] text-slate-400 mt-2">
            {sig.signer_name}
            {sig.auto_applied && <span className="ml-1 text-violet-600 font-semibold">· signature auto-apposée</span>}
            {sig.signed_at && <> · {formatDateFR(sig.signed_at?.split("T")[0])}</>}
          </div>
          {sig.ip_address && (
            <div className="text-[10px] text-slate-400">
              IP : {sig.ip_address}
            </div>
          )}
        </>
      ) : (
        <div className="h-16 flex items-center justify-center text-xs text-slate-400">En attente de signature</div>
      )}
    </div>
  );
}
